import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { PageHeader } from 'ui/pageHeader';
import { Table } from 'ui/table';
import { PaginationFooter } from 'ui/paginationFooter';
import { useClientPaginatedState } from 'ui/table/hooks/useClientPaginatedState';
import { TableHeader } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';
import { InputText } from 'ui/inputs/inputText';
import { Skeleton } from 'ui/skeleton';
import { addWarningToast } from 'modules/toastContainer';
import { selectProjects, selectOrganizations, selectIsLoading } from '../selectors';
import { fetchProjectsRequest, updateProjectRequest } from '../actions';
import { configureColumns, projectTableFilterFn } from '../utils';
import EditModal from './EditModal';
import { ArchiveModal } from './ArchiveModal';
import { UnarchiveModal } from './UnarchiveModal';

export const Projects = () => {
  const dispatch = useDispatch();
  const projects = useSelector(selectProjects);
  const organizations = useSelector(selectOrganizations);
  const isLoading = useSelector(selectIsLoading);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isUnarchiveModalOpen, setIsUnarchiveModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState({});

  const canOpenArchiveModal = (projectName) => projectName.toLowerCase().includes('archive');

  useEffect(() => {
    dispatch(fetchProjectsRequest());
  }, [dispatch]);

  const closeEditModal = () => {
    setIsEditModalOpen(false);
  };

  const closeArchiveModal = () => {
    setIsArchiveModalOpen(false);
  };

  const closeUnarchiveModal = () => {
    setIsUnarchiveModalOpen(false);
  };

  const closeAllModals = () => {
    setSelectedProject({});
    setIsEditModalOpen(false);
    setIsArchiveModalOpen(false);
    setIsUnarchiveModalOpen(false);
  };

  const openEditModal = async (project) => {
    // Using async/await here because there has been a race condition where the modal was opened
    // before the project was selected therefore the EditModal was showing wrong configs.
    // Here asnc/await is used to make sure async methods are called and returned in the desired order.
    await closeAllModals();
    await setSelectedProject(project);
    await setIsEditModalOpen(true);
  };

  const openArchiveModal = async (project) => {
    // Async/await used in order to be safe and consistent. Check comment at openEditModal above.
    if (canOpenArchiveModal(project.name)) {
      await closeAllModals();
      await setSelectedProject(project);
      await setIsArchiveModalOpen(true);
    } else {
      await dispatch(
        addWarningToast(
          'You must rename the project to include the text “archive” before archiving it.',
        ),
      );
    }
  };

  const openUnarchiveModal = async (project) => {
    // Async/await used in order to be safe and consistent. Check comment at openEditModal above.
    await closeAllModals();
    await setSelectedProject(project);
    await setIsUnarchiveModalOpen(true);
  };

  const updateProject = (project) => {
    dispatch(updateProjectRequest(project));
  };

  const organization = (id) => organizations.find((e) => e.id === id);

  const COLUMNS = configureColumns({
    organizations,
    openEditModal,
    openArchiveModal,
    openUnarchiveModal,
  });

  const {
    currentPage,
    filter,
    itemsPerPage,
    numItems,
    rows,
    setCurrentPage,
    setFilter,
    setItemsPerPage,
    setSortDirection,
    setSortedBy,
    sortDirection,
    sortedBy,
  } = useClientPaginatedState({
    rows: projects,
    sortedBy: 'id',
    filterFn: projectTableFilterFn,
  });

  return (
    <>
      <EditModal
        project={selectedProject}
        closeModal={closeEditModal}
        open={isEditModalOpen}
        handleAction={updateProject}
      />
      <ArchiveModal
        organizationName={get(organization(selectedProject.organizationId), 'name')}
        project={selectedProject}
        closeModal={closeArchiveModal}
        open={isArchiveModalOpen}
        handleAction={updateProject}
      />
      <UnarchiveModal
        organizationName={get(organization(selectedProject.organizationId), 'name')}
        project={selectedProject}
        closeModal={closeUnarchiveModal}
        open={isUnarchiveModalOpen}
        handleAction={updateProject}
      />
      <PageHeader
        pageActions={
          <InputText
            data-test="filter-projects"
            value={filter}
            onChange={setFilter}
            placeholder="Filter projects..."
          />
        }
      >
        Projects
      </PageHeader>
      {isLoading ? (
        <Skeleton height={400} />
      ) : (
        <>
          <Table>
            <TableHeader
              columns={COLUMNS}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              sortedBy={sortedBy}
              setSortedBy={setSortedBy}
            />
            <TableRows columns={COLUMNS} rows={rows} />
          </Table>
          <PaginationFooter
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            numItems={numItems}
            onUpdate={({ page, pageSize }) => {
              setCurrentPage(page);
              setItemsPerPage(pageSize);
            }}
          />
        </>
      )}
    </>
  );
};
